import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { Download, Filter, Loader2, X, Mail, FileText, Compass, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { trackResourceLead } from '../lib/analytics';
import { REFRANES_GUIA } from '../config/refranesGuia';
import { VOCAB_HUB, VOCAB_TOPICS } from '../config/vocabulario';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Camino de Santiago infographics — free, no email gating, link to the dedicated
// mobile-friendly page for pinch-to-zoom + PDF download.
const CAMINO_INFOGRAPHICS = [
  {
    id: 'camino-1',
    slug: 'infografia-1',
    title: 'Expresiones y vocabulario del Camino',
    description:
      'Palabras esenciales en español para tu paso por el Camino: expresiones, personas, lugares, verbos útiles.',
    accent: '#F5A6A4',
    category: 'vocabulary',
  },
  {
    id: 'camino-2',
    slug: 'infografia-2',
    title: '10 frases útiles para el Camino de Santiago',
    description:
      'Las 10 frases más útiles para pedir agua, comida, sello, información y comunicarte con otros peregrinos.',
    accent: '#A8D8EA',
    category: 'vocabulary',
  },
  {
    id: 'camino-3',
    slug: 'infografia-3',
    title: 'El Camino de Santiago: rutas e historia',
    description:
      'Guía visual: rutas principales, credencial, Compostela, qué llevar y consejos para peregrinos.',
    accent: '#7DBDD9',
    category: 'guides',
  },
];

const VOCAB_LABEL = {
  es: 'Vocabulario de español por temas',
  de: 'Spanischer Wortschatz nach Themen',
  en: 'Spanish vocabulary by topic',
};

export const Resources = () => {
  const { t, language } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [activeFilter, setActiveFilter] = useState('all');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Email capture modal state
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const { data } = await axios.get(`${API}/materials`);
        setMaterials(data);
      } catch (err) {
        console.error('Error fetching materials:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, []);

  const filters = [
    { id: 'all', label: t.resources.filters.all },
    { id: 'grammar', label: t.resources.filters.grammar },
    { id: 'vocabulary', label: t.resources.filters.vocabulary },
    { id: 'exercises', label: t.resources.filters.exercises },
    { id: 'guides', label: t.resources.filters.guides }
  ];

  const filteredResources = activeFilter === 'all'
    ? materials
    : materials.filter(resource => resource.category === activeFilter);

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-700 border-green-300',
      'A2': 'bg-blue-100 text-blue-700 border-blue-300',
      'B1': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'B2': 'bg-orange-100 text-orange-700 border-orange-300',
      'B1-C1': 'bg-orange-100 text-orange-700 border-orange-300',
      'C1': 'bg-red-100 text-red-700 border-red-300',
      'C2': 'bg-purple-100 text-purple-700 border-purple-300'
    };
    return colors[level] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const openDownloadModal = (material) => {
    setSelectedMaterial(material);
    setEmail('');
    setName('');
    setError('');
  };

  const handleConfirmDownload = async () => {
    setError('');
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Por favor introduce un email válido.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/materials/${selectedMaterial.id}/download`,
        { email: email.trim(), name: name.trim() || null },
        { responseType: 'blob' }
      );
      // Trigger browser download of the returned PDF
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = selectedMaterial.original_filename || `${selectedMaterial.title}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);

      // Update local downloads count and close modal
      trackResourceLead(`material_${selectedMaterial.slug || selectedMaterial.title}`);
      setMaterials((prev) => prev.map((m) => m.id === selectedMaterial.id ? { ...m, downloads: m.downloads + 1 } : m));
      setSelectedMaterial(null);
    } catch (err) {
      console.error('Download error:', err);
      setError('Hubo un error al descargar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="resources" className="py-24 bg-gradient-to-b from-[#E8F5FA] to-white relative overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.resources.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6">
            {t.resources.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
          {t.resources.lead && (
            <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl mx-auto" data-testid="resources-lead">
              {t.resources.lead}
            </p>
          )}
        </motion.div>

        {/* Filters */}
        {materials.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center mb-12 flex-wrap gap-3"
          >
            <Filter className="w-5 h-5 text-gray-500" />
            {filters.map((filter) => (
              <motion.button
                key={filter.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`resource-filter-${filter.id}`}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#A8D8EA]'
                }`}
              >
                {filter.label}
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
          </div>
        )}

        {/* Resources Grid */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {/* Featured Camino de Santiago infographics — no email gating */}
            {(activeFilter === 'all' ||
              activeFilter === 'vocabulary' ||
              activeFilter === 'guides') &&
              CAMINO_INFOGRAPHICS.filter(
                (c) => activeFilter === 'all' || c.category === activeFilter
              ).map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  data-testid={`camino-card-${c.slug}`}
                  className="relative overflow-hidden rounded-2xl p-6 shadow-lg border border-white/40 group text-white"
                  style={{
                    background: `linear-gradient(135deg, ${c.accent} 0%, #7DBDD9 100%)`,
                  }}
                >
                  <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/15 rounded-full pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 rounded-full bg-white/25 backdrop-blur text-xs font-bold tracking-wide">
                        <Compass className="inline w-3 h-3 mr-1 -mt-0.5" />
                        CAMINO DE SANTIAGO
                      </span>
                    </div>
                    <h3 className="text-lg font-bold mb-3 leading-tight">{c.title}</h3>
                    <p className="text-sm text-white/90 mb-6 line-clamp-3">{c.description}</p>
                    <Link
                      to={`/camino/${c.slug}`}
                      data-testid={`camino-open-${c.slug}`}
                      className="w-full py-3 bg-white text-[#2F2F2F] rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
                    >
                      Ver infografía
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}

            {/* Hub de vocabulario por temas (A1 y A2) con página propia por tema */}
            {(activeFilter === 'all' || activeFilter === 'vocabulary') && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                data-testid="resource-card-vocabulario"
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-bold border bg-green-100 text-green-700 border-green-300">
                    A1-A2
                  </span>
                  <span className="text-xs text-gray-500">{VOCAB_TOPICS.length} fichas</span>
                </div>

                <h3 className="text-lg font-bold text-[#4A4A4A] mb-3 group-hover:text-[#7DBDD9] transition-colors">
                  {VOCAB_LABEL[language] || VOCAB_LABEL.es}
                </h3>

                <p className="text-sm text-gray-600 mb-6 line-clamp-3">
                  Fichas de vocabulario A1 y A2 con traducción al inglés y al alemán: familia, profesiones, casa,
                  comida, viajes, salud y mucho más. Cada tema con ejemplos y PDF descargable.
                </p>

                <Link
                  to={VOCAB_HUB.path}
                  data-testid="resource-open-vocabulario"
                  className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Ver vocabulario</span>
                </Link>
              </motion.div>
            )}

            {/* Guía de refranes — landing propia con captación de email */}
            {(activeFilter === 'all' || activeFilter === REFRANES_GUIA.category) && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                data-testid="resource-card-refranes"
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getLevelColor(REFRANES_GUIA.level)}`}>
                    {REFRANES_GUIA.level}
                  </span>
                  <span className="text-xs text-gray-500">{REFRANES_GUIA.format}</span>
                </div>

                <h3 className="text-lg font-bold text-[#4A4A4A] mb-3 group-hover:text-[#7DBDD9] transition-colors">
                  {REFRANES_GUIA.title}
                </h3>

                <p className="text-sm text-gray-600 mb-6 line-clamp-3">
                  {REFRANES_GUIA.description}
                </p>

                <Link
                  to={`/recursos/${REFRANES_GUIA.slug}`}
                  data-testid="resource-open-refranes"
                  className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar gratis</span>
                </Link>
              </motion.div>
            )}

            {filteredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ y: -8, scale: 1.02 }}
                data-testid={`resource-card-${index}`}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getLevelColor(resource.level)}`}>
                    {resource.level}
                  </span>
                  <span className="text-xs text-gray-500">{resource.downloads} {t.resources.downloadsLabel}</span>
                </div>

                <h3 className="text-lg font-bold text-[#4A4A4A] mb-3 group-hover:text-[#7DBDD9] transition-colors">
                  {resource.title}
                </h3>

                <p className="text-sm text-gray-600 mb-6 line-clamp-3">
                  {resource.description}
                </p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => openDownloadModal(resource)}
                  data-testid={`resource-download-${index}`}
                  className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  <span>{t.resources.download}</span>
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredResources.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <FileText className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg">
              {materials.length === 0 ? 'Muy pronto encontrarás aquí materiales para descargar.' : t.resources.emptyState}
            </p>
          </motion.div>
        )}
      </div>

      {/* Email Capture Modal */}
      <AnimatePresence>
        {selectedMaterial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelectedMaterial(null)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            data-testid="download-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] p-6 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Descarga gratuita</h3>
                <button onClick={() => setSelectedMaterial(null)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-gray-100" data-testid="download-modal-close">
                  <X className="w-5 h-5 text-[#4A4A4A]" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-1">Estás a punto de descargar:</p>
                <p className="font-bold text-[#4A4A4A] mb-5">{selectedMaterial.title}</p>
                <p className="text-sm text-gray-500 mb-4">Déjanos tu email y te enviaremos también recursos y novedades de la academia.</p>

                <div className="space-y-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="download-name-input"
                    placeholder="Tu nombre (opcional)"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                  />
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="download-email-input"
                      placeholder="tu@email.com"
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                    />
                  </div>
                </div>

                {error && <p className="text-red-500 text-sm mt-3" data-testid="download-error">{error}</p>}

                <button
                  onClick={handleConfirmDownload}
                  disabled={submitting}
                  data-testid="download-confirm-btn"
                  className="w-full mt-5 py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />Descargando...</> : <><Download className="w-5 h-5" />Descargar PDF</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
